import { cn, Text } from "@aurem/ui";
import { use } from "react";
import { useTranslations } from "../i18n";
import { Dirham } from "../icons";
import { LanguageCurrencyPickerContext } from "./context";
import type { Currency, TranslationMap } from "./types";

type CurrencyTabProps = {
  currencies: Currency[];
  currentCurrency: string;
  onCurrencySelect: (id: string) => void;
};

const CurrencyTab = ({
  currencies,
  currentCurrency,
  onCurrencySelect,
}: CurrencyTabProps) => {
  const { t } = useTranslations();
  const { setIsOpen } = use(LanguageCurrencyPickerContext);

  const translationMap: TranslationMap = {
    AED: {
      label: t("components.languageCurrencyPicker.currency.aed"),
      description: (
        <div className="flex items-center gap-0.5">
          <span>AED</span>
          {"-"}
          <Dirham className="w-3 h-3" />
        </div>
      ),
    },
    USD: {
      label: t("components.languageCurrencyPicker.currency.usd"),
      description: "USD - $",
    },
    GBP: {
      label: t("components.languageCurrencyPicker.currency.gbp"),
      description: "GBP - £",
    },
    EUR: {
      label: t("components.languageCurrencyPicker.currency.eur"),
      description: "EUR - €",
    },
  };

  const onSelect = (id: string) => {
    return () => {
      onCurrencySelect(id);
      setIsOpen(false);
    };
  };

  return (
    <div className="flex flex-col gap-4">
      <Text size="md" weight="medium">
        {t("components.languageCurrencyPicker.chooseDefaultCurrency")}
      </Text>
      <div className="flex flex-col gap-2">
        {currencies.map((currency) => (
          <button
            key={currency.id}
            onClick={onSelect(currency.id)}
            className={cn(
              "flex justify-between items-center px-4 rounded-xl h-[52px]",
              "border border-neutral-500/5 hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 active:bg-neutral-500/10",
              "focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
              currentCurrency === currency.id && "border-primary-500",
            )}
            style={{ outlineOffset: "1px" }}
          >
            <Text as="span" size="md">
              {translationMap[currency.id].label}
            </Text>
            <Text as="span" size="sm" className="text-neutral-500/50">
              {translationMap[currency.id].description}
            </Text>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CurrencyTab;
